<template>
    <div>
        <SrDropzoneUpload
            id="video-upload-dropzone"
            ref="uploadDropzone"
            :dropzone-options="dropzoneOptions"
            upload-button-title="Upload .mp4 video"
            disable-file-list
            @vdropzone-file-added="onFileAdded"
            @vdropzone-removed-file="onFileRemoved"
            @uploadSuccess="onUploadSucceeded"
            @vdropzone-error="onUploadFailed"
            @vdropzone-sending="onSending"
        />
        <ErrorBox :show="Boolean(errorMessage)" :message="errorMessage" class="mt-6" />
        <VastGenerationStatus v-model="isStatusDialogShown" :status="vastGenerationStatus" />
    </div>
</template>
<script>
import { computed, inject, ref } from 'vue';
import { SrDropzoneUpload } from '@ads/design-system';
import { videoUploadServiceKey } from '@/injection-keys';
import getVideoDropzoneOptions from '@/components/domain/creatives/video/video-dropzone/getVideoDropzoneOptions';
import VastGenerationStatus from '@/components/domain/creatives/video/video-dropzone/VastGenerationStatus';
import { VAST_GENERATION_STATUS } from '@/services/video/VAST_GENERATION_STATUS';
import ErrorBox from '@/components/domain/creatives/shared/ErrorBox';

export default {
    name: 'VideoDropzone',
    components: {
        ErrorBox,
        VastGenerationStatus,
        SrDropzoneUpload,
    },
    props: {
        clickUrl: {
            type: String,
            required: false,
        },
    },
    setup(props, { emit }) {
        const videoUploadService = inject(videoUploadServiceKey);

        const dropzoneOptions = getVideoDropzoneOptions();
        const uploadDropzone = ref(null);
        const vueDropzone = computed(() => uploadDropzone.value.$refs.vueDropzone);

        const currentVideoId = ref(null);
        const currentVastUrl = ref(null);
        const currentFile = ref(null);

        const pollingInterval = ref(null);
        const vastGenerationStatus = ref();
        const isStatusDialogShown = ref(false);

        const errorMessage = ref();
        const clearErrorMessage = () => {
            errorMessage.value = null;
        };

        const deleteVideoFile = async () => {
            await videoUploadService.deleteUploadedVideo(currentVideoId.value);
        };

        const removeAllFilesFromDropzone = () => {
            vueDropzone.value.removeAllFiles();
        };

        const replaceCurrentFile = (file) => {
            if (currentFile.value) {
                vueDropzone.value.removeFile(currentFile.value);
            }
            currentFile.value = file;
        };
        const onFileAdded = async (file) => {
            clearErrorMessage();
            replaceCurrentFile(file);
            try {
                const uploadInfo = await videoUploadService.prepareForUpload(file, props.clickUrl);
                currentVideoId.value = uploadInfo.videoId;
                currentVastUrl.value = uploadInfo.vastFileUrl;
                vueDropzone.value.setOption('url', uploadInfo.uploadUrl);
                vueDropzone.value.processQueue();
            } catch (error) {
                errorMessage.value = error.message;
                removeAllFilesFromDropzone();
            }
        };

        const onFileRemoved = async () => {
            /**
             * vue2-dropzone triggers vdropzone-removed-file when component is unmounted,
             * but we only want to delete the file if the user clicked on the remove button.
             * https://github.com/rowanwins/vue-dropzone/issues/85#issuecomment-494270734
             */
            const wasRemoveButtonClicked = vueDropzone.value.dropzone.disabled !== true;
            if (wasRemoveButtonClicked && currentVideoId.value) {
                emit('update:vastUrl', null);
                await deleteVideoFile();
            }
        };

        const isEndStatus = (newStatus) =>
            [VAST_GENERATION_STATUS.SUCCEEDED, VAST_GENERATION_STATUS.FAILED, VAST_GENERATION_STATUS.ABORTED].includes(newStatus);
        const stopPolling = () => {
            clearInterval(pollingInterval.value);
            pollingInterval.value = null;
        };
        const handleNewStatusReceived = async (newStatus) => {
            vastGenerationStatus.value = newStatus;
            if (isEndStatus(newStatus)) {
                stopPolling();
            }
            if (newStatus === VAST_GENERATION_STATUS.SUCCEEDED) {
                emit('update:vastUrl', currentVastUrl.value);
            }
            if (newStatus === VAST_GENERATION_STATUS.FAILED || newStatus === VAST_GENERATION_STATUS.ABORTED) {
                removeAllFilesFromDropzone();
                await deleteVideoFile();
            }
        };
        const refreshStatus = async () => {
            try {
                const newStatus = await videoUploadService.getVastGenerationStatus(currentVideoId.value);
                await handleNewStatusReceived(newStatus);
            } catch (error) {
                errorMessage.value = error.message;
                isStatusDialogShown.value = false;
                stopPolling();
            }
        };
        const onUploadSucceeded = async () => {
            vastGenerationStatus.value = null;
            isStatusDialogShown.value = true;
            await refreshStatus();
            pollingInterval.value = setInterval(refreshStatus, 1000);
        };

        const onUploadFailed = () => {
            errorMessage.value = 'Failed to upload the video. Please try again.';
        };

        const onSending = (file, xhr) => {
            const originalSend = xhr.send;
            xhr.send = function () {
                originalSend.call(xhr, file);
            };
        };

        return {
            uploadDropzone,
            dropzoneOptions,
            vastGenerationStatus,
            isStatusDialogShown,
            errorMessage,
            onFileAdded,
            onFileRemoved,
            onUploadSucceeded,
            onUploadFailed,
            onSending,
        };
    },
};
</script>
